import type { JSX } from 'react';
import { useRouterState } from '@tanstack/react-router';

/**
 * Barre de progression de NAVIGATION du shell.
 *
 * Filet fixe tout en haut de la fenetre (0.125rem = 2px), fond accent. Elle
 * n'apparait que pendant les transitions de route EN ATTENTE : quand le
 * routeur charge le chunk paresseux d'une app (clic dock / lanceur) ou tout
 * autre chargement de route, `useRouterState` passe `status` a "pending" et la
 * barre s'affiche ; des que la route est prete ("idle") elle disparait en
 * fondu. Le preload d'intention du routeur fait qu'une app deja chargee
 * n'affiche rien (rien a attendre) : c'est voulu.
 *
 * Doctrine (socle) : la barre = navigation. Le CONTENU d'une app affiche son
 * squelette en zone 5 via le gabarit ; on ne pose jamais de squelette plein
 * ecran a la place de cette barre. Aucune dependance (pas de NProgress) : CSS
 * pur sur les tokens du socle (--accent, --dur-*, --ease-standard). Le balayage
 * perpetuel est lineaire, comme le spinner ; le fondu d'apparition/disparition
 * reste une transition subtile (--dur-base). Reduced motion : plus de balayage,
 * un simple battement d'opacite (meme parti pris que le spinner du socle).
 */

const PROGRESS_CSS = `
.taki-progress {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 0.125rem;
  z-index: 100;
  pointer-events: none;
  overflow: hidden;
  opacity: 0;
  transition: opacity var(--dur-base) var(--ease-standard);
}
.taki-progress[data-active='true'] {
  opacity: 1;
}
.taki-progress__bar {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 40%;
  background-color: var(--accent);
  transform: translateX(-100%);
  will-change: transform;
  animation-name: taki-progress-sweep;
  animation-duration: calc(var(--dur-slow) * 4);
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  animation-play-state: paused;
}
.taki-progress[data-active='true'] .taki-progress__bar {
  animation-play-state: running;
}
@keyframes taki-progress-sweep {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
}
@media (prefers-reduced-motion: reduce) {
  .taki-progress__bar {
    width: 100%;
    transform: none;
    animation-name: taki-progress-pulse;
    animation-duration: calc(var(--dur-slow) * 5);
    animation-timing-function: var(--ease-standard);
  }
  @keyframes taki-progress-pulse {
    0%, 100% { opacity: 0.45; }
    50% { opacity: 1; }
  }
}
`;

/**
 * Barre de progression de navigation. Se monte une seule fois dans le shell
 * (au-dessus de tout, z-index eleve, non focusable, decorative). Ne prend
 * aucune prop : elle lit l'etat du routeur elle-meme.
 */
export function TakiProgressBar(): JSX.Element {
  const pending = useRouterState({ select: (state) => state.status === 'pending' });
  return (
    <>
      <style>{PROGRESS_CSS}</style>
      {/* Decorative : le feedback accessible vient du contenu de destination
          (gabarit aria-busy + squelette de zone 5), pas de ce filet. */}
      <div className="taki-progress" data-active={pending} aria-hidden>
        <span className="taki-progress__bar" />
      </div>
    </>
  );
}
